// For analytics purposes, we can get the user's sign in type from their username
import { Auth } from 'aws-amplify'
import { Logger } from '@meprism/app-utils'
import {
  AuthenticationActions,
  logOutUser,
  postConfirmLogin,
  refreshEntitlementsFromSession,
} from './authenticationSlice'
import { BaseThunkExtra } from '../base'
import { AnyAction, ThunkAction } from '@reduxjs/toolkit'
import { AmplifyAuthEvent, AuthenticationState } from './authenticationTypes'

type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  { authentication: AuthenticationState },
  BaseThunkExtra,
  AnyAction
>


export type CreateAmplifyListenerProps = {
  onAutoSignInFailure: () => void
}

export const createAmplifyListener =
  (_props: CreateAmplifyListenerProps): AppThunk =>
  (dispatch, getState, extra) => {
    return extra.Hub.listen('auth', (capsule) => {
      const { channel, payload } = capsule
      // this shouldn't be necessary but you can't be too careful these days
      if (channel !== 'auth') {
        return
      }
      const { event, data } = payload
      if (!event) {
        return
      }
      Logger.debug(`Amplify auth event: ${event}`)
      switch (event) {
        case 'autoSignIn':
          Logger.info('Auto signin')
          // nothing else to do here - this event is always accompanied by the signIn event below
          // and the real work happens there.
          // UNLESS we need to handle some MFA challenges or similar
          break
        case 'signIn':
          // sign in actions are handled here, because we don't have direct control over
          // when federated signin happens, best just to react to the event when it does
          Auth.currentAuthenticatedUser()
            .then((user) => {
              return dispatch(postConfirmLogin(user))
            })
            .catch(() => {
              // First-time federated users may not have custom:buid in the initial token
              // because the pre-auth Lambda sets it AFTER the token is generated.
              // Retry with bypassCache to get the refreshed token with updated attributes.
              Logger.info('Retrying auth with cache bypass for federated user')
              return Auth.currentAuthenticatedUser({ bypassCache: true })
                .then((refreshedUser) => dispatch(postConfirmLogin(refreshedUser)))
            })
            .then(() => {
              Logger.info('User Sign In')
            })
            .catch((error) => {
              Logger.error(`Error handling signin: ${error}`)
              dispatch(AuthenticationActions.removeBlockingRequest('SIGNIN'))
            })
          break
        case 'signOut':
          // not much to do here, actions on signout are handled via dispatch
          break
        case 'forgotPassword':
          extra.AnalyticsManager.trackTypedEvent({
            event: 'Forgot Password - Portal',
            source: 'Business Portal'
          })
          break
        case 'tokenRefresh':
          // on token refresh, pass data from the token to Redux to keep it in sync
          dispatch(refreshEntitlementsFromSession())
          break
        case 'tokenRefresh_failure':
          const state = getState()
          if (state?.authentication?.muid) {
            Logger.error(`Cognito token refresh failure ${data}`)
            extra.Toast.show({
              type: 'error',
              text1: 'Your session expired',
            })
            dispatch(logOutUser())
          } else {
            // token refresh failures not logged in I THINK are something wrong
            // but important thing is we should not be infinite looping our toasts
            extra.AnalyticsManager.error('TokenRefreshFailure')
          }
          break

        case 'updateUserAttributes_failure':
          extra.Toast.show({
            type: 'error',
            text1: 'Your attributes could not be saved',
          })
          break
        case 'updateUserAttributes':
          Logger.info(`UpdateUserAttributes: ${JSON.stringify(data)}`)
          break
        case 'verify':
          extra.Toast.show({
            type: 'success',
            text1: 'Your MFA settings have been saved',
          })
          break
      }
      dispatch(
        AuthenticationActions.emitAmplifyEvent(event as AmplifyAuthEvent),
      )
    })
  }
